import '@szhsin/react-menu/dist/index.css'
import * as React from 'react'
import {
  ControlledMenu,
  MenuItem,
  SubMenu,
  MenuDivider,
} from '@szhsin/react-menu'
import {
  ActivityType,
  ActivityTypeId,
  ChartScanActivity,
} from '@/lib/api/company/chartScan'
import { activityLabel, useChartT } from '../i18n'

type Props = {
  position: { x: number; y: number } | null
  onClose: () => void
  selectionCount: number
  target: ChartScanActivity | null
  onRemove: () => void
  onAdd: (type: ActivityTypeId) => void
  onChangeType: (type: ActivityTypeId) => void
  onToggleFerry: () => void
  onDeleteAllOfType: (type: ActivityTypeId) => void
  onVehicleChange?: () => void
  onEditVehicleChange?: () => void
  onRemoveVehicleChange?: () => void
  onCountryChange: () => void
  onSetDayStart?: () => void
  onSetDayEnd?: () => void
  onSetBeginCountry: () => void
  onSetEndCountry: () => void
}

const types: ActivityTypeId[] = [
  ActivityType.Driving,
  ActivityType.Work,
  ActivityType.Availability,
  ActivityType.Rest,
  ActivityType.NoData,
]
const itemClass = 'tw-my-0 tw-px-4 tw-py-1 tw-text-sm tw-text-content-base'

export function ActivityContextMenu(p: Props) {
  const T = useChartT()
  const single = !!p.target && p.selectionCount === 1
  const selected = !!p.target && p.selectionCount > 0
  const run = (action?: () => void) => () => {
    action?.()
    p.onClose()
  }
  return (
    <ControlledMenu
      anchorPoint={p.position ?? undefined}
      state={p.position ? 'open' : 'closed'}
      onClose={p.onClose}
      portal
      className="tw-z-[10000]"
      boundingBoxPadding="8"
      overflow="auto"
      onContextMenu={(e) => e.preventDefault()}
      onKeyDown={(e) => {
        const type = e.key === 'F2' ? ActivityType.Work : e.key === 'F3' ? ActivityType.Availability : e.key === 'F4' ? ActivityType.Driving : null
        if (type == null) return
        e.preventDefault()
        e.stopPropagation()
        p.onDeleteAllOfType(type)
        p.onClose()
      }}
    >
      <MenuItem
        className={itemClass}
        disabled={!selected}
        onClick={run(p.onRemove)}
      >
        {p.selectionCount > 1
          ? T('removeActivities', 'Usuń aktywności')
          : T('removeActivity', 'Usuń aktywność')}
      </MenuItem>
      <SubMenu
        portal
        label={T('contextAddEvent', 'Dodaj zdarzenie')}
        disabled={!single}
      >
        {types.map((type) => (
          <MenuItem
            className={itemClass}
            key={type}
            onClick={run(() => p.onAdd(type))}
          >
            {activityLabel(T, type)}
          </MenuItem>
        ))}
      </SubMenu>
      <SubMenu
        portal
        label={T('contextChangeEvent', 'Zmień zdarzenie')}
        disabled={!selected}
      >
        {types.map((type) => (
          <MenuItem
            className={itemClass}
            key={type}
            onClick={run(() => p.onChangeType(type))}
          >
            {activityLabel(T, type)}
          </MenuItem>
        ))}
      </SubMenu>
      <MenuDivider />
      <MenuItem
        className={itemClass}
        type="checkbox"
        checked={!!p.target?.ferryTrainCrossing}
        disabled={!selected}
        onClick={run(p.onToggleFerry)}
      >
        {T('contextFerry', 'Prom')}
      </MenuItem>
      <MenuDivider />
      <MenuItem
        className={itemClass}
        disabled={!p.onSetDayStart || p.selectionCount > 1}
        onClick={run(p.onSetDayStart)}
      >
        {T('setDayStart', 'Ustaw początek dnia')}
      </MenuItem>
      <MenuItem
        className={itemClass}
        disabled={p.selectionCount > 1}
        onClick={run(p.onSetBeginCountry)}
      >
        {T('setBeginCountry', 'Ustaw kraj początku dnia')}
      </MenuItem>
      <MenuItem
        className={itemClass}
        disabled={!p.onSetDayEnd || p.selectionCount > 1}
        onClick={run(p.onSetDayEnd)}
      >
        {T('setDayEnd', 'Ustaw koniec dnia')}
      </MenuItem>
      <MenuItem
        className={itemClass}
        disabled={p.selectionCount > 1}
        onClick={run(p.onSetEndCountry)}
      >
        {T('setEndCountry', 'Ustaw kraj końca dnia')}
      </MenuItem>
      <MenuDivider />
      {p.onVehicleChange && (
        <MenuItem
          className={itemClass}
          disabled={!single}
          onClick={run(p.onVehicleChange)}
        >
          {T('contextChangeVehicle', 'Zmień pojazd')}
        </MenuItem>
      )}
      {p.onEditVehicleChange && (
        <MenuItem
          className={itemClass}
          disabled={!single}
          onClick={run(p.onEditVehicleChange)}
        >
          {T('editVehicleChange', 'Edytuj zmianę pojazdu')}
        </MenuItem>
      )}
      {p.onRemoveVehicleChange && (
        <MenuItem
          className={itemClass}
          disabled={!single}
          onClick={run(p.onRemoveVehicleChange)}
        >
          {T('removeVehicleChange', 'Usuń zmianę pojazdu')}
        </MenuItem>
      )}
      <MenuItem
        className={itemClass}
        disabled={!single}
        onClick={run(p.onCountryChange)}
      >
        {T('insertBorderCrossing', 'Wstaw przekroczenie granicy')}
      </MenuItem>
      <MenuDivider />
      {[ActivityType.Work, ActivityType.Availability, ActivityType.Driving].map(
        (type, index) => (
          <MenuItem
            className={itemClass}
            key={type}
            onClick={run(() => p.onDeleteAllOfType(type))}
          >
            <span>
              {T('removeAllOfType', 'Usuń wszystkie typu')}{' '}
              {type === ActivityType.Work ? T('contextOtherWork', 'Inna praca') : activityLabel(T, type)}
            </span>
            <span className="tw-ml-6 tw-text-xs tw-text-content-secondary">
              F{index + 2}
            </span>
          </MenuItem>
        ),
      )}
    </ControlledMenu>
  )
}
